import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dataService } from '@/services/dataService';
import { Agent } from '@/types/entities';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';
import { AgentFormDrawer, AgentFormValues } from './AgentFormDrawer';
import { getDepartmentDefinition } from '@/config/agentDepartments.config';

export function AgentsPage() {
  const { t } = useI18n();
  const push = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [formOpen, setFormOpen] = useState(searchParams.get('new') === '1');
  const [editTarget, setEditTarget] = useState<Agent | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null);

  function reload() {
    dataService.listAgents().then(setAgents);
  }
  useEffect(reload, []);

  async function handleCreate(values: AgentFormValues) {
    const created = await dataService.createAgent(values);
    push(`${t('toast.agentAdded')} — ${created.name}`, 'success');
    setFormOpen(false);
    setSearchParams({});
    reload();
  }
  async function handleEdit(values: AgentFormValues) {
    if (!editTarget) return;
    await dataService.updateAgent(editTarget.id, values);
    push(t('toast.agentUpdated'), 'success');
    setEditTarget(null);
    reload();
  }
  async function togglePause(a: Agent) {
    await dataService.toggleAgentPause(a.id);
    reload();
  }
  async function archive(a: Agent) {
    await dataService.archiveAgent(a.id);
    push(t('toast.agentArchived'), 'warn');
    reload();
  }
  async function confirmDelete() {
    if (!deleteTarget) return;
    await dataService.deleteAgent(deleteTarget.id);
    push(t('toast.demoRecordDeleted'), 'error');
    setDeleteTarget(null);
    reload();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('nav.agents')}</h1>
        <button className="btn-primary" onClick={() => setFormOpen(true)}>+ {t('agentForm.newAgent')}</button>
      </div>
      <div className="card" style={{ padding: 8 }}>
        <div className="table-scroll">
        <table className="dt">
          <thead><tr><th>Name</th><th>Department</th><th>Provider</th><th>Model</th><th>Permission</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {agents.map((a) => (
              <tr key={a.id}>
                <td>{a.name}<div style={{ fontSize: 11, color: 'var(--text-3)' }}>{a.role}</div></td>
                <td>{a.department ? t(getDepartmentDefinition(a.department)?.nameKey ?? '') || a.department : '—'}</td>
                <td>{a.provider}{!a.isPreviewCreated && <span className="soon-tag-inline" style={{ marginInlineStart: 6 }}>{t('common.awaitingCredentials')}</span>}</td>
                <td>{a.model}</td>
                <td><span className={`badge ${a.permissionLevel === 3 ? 'crit' : a.permissionLevel === 2 ? 'warn' : 'info'}`}>{t(`permission.level${a.permissionLevel}`)}</span></td>
                <td><StatusBadge entity="agent" statusKey={a.status} /></td>
                <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => setEditTarget(a)}>{t('rowMenu.edit')}</button>
                  <button onClick={() => togglePause(a)}>{a.status === 'paused' ? t('agentForm.resume') : t('agentForm.pause')}</button>
                  <button style={{ color: 'var(--warn)' }} onClick={() => archive(a)}>{t('rowMenu.archive')}</button>
                  {a.isPreviewCreated && <button style={{ color: 'var(--crit)' }} onClick={() => setDeleteTarget(a)}>{t('rowMenu.deleteDemo')}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {formOpen && <AgentFormDrawer onClose={() => { setFormOpen(false); setSearchParams({}); }} onSubmit={handleCreate} />}
      {editTarget && <AgentFormDrawer agent={editTarget} onClose={() => setEditTarget(null)} onSubmit={handleEdit} />}

      <ConfirmModal
        open={!!deleteTarget}
        title={t('confirm.deleteAgentTitle')}
        recordName={deleteTarget?.name}
        description={t('confirm.deleteAgentDesc')}
        reversible={false}
        tone="destructive"
        confirmLabel={t('rowMenu.deleteDemo')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
